import { useEffect, useRef, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { useDrawerOptional } from '../components/DrawerSystem';
import { useSolutionContext } from '../contexts/SolutionContext';
import ChatPanel from './ChatPanel';
import { API_ENDPOINTS } from '../config/api';
import { getUserId } from './utils/getUserId';
import { useTranslation } from '../hooks/useTranslation.jsx';

// Once the user opens the chat, the onboarding bubble has done its job.
// We stash a flag in localStorage so it doesn't pop back on every navigation.
// It's cleared again only when the user clicks "Tyhjennä keskustelu" inside
// ChatPanel (which dispatches a kuutio:chat-cleared event).
const CHAT_DISCOVERED_KEY = 'kuutio_chat_discovered';

/**
 * ChatDrawerTab — registers the Chat tab in the drawer system.
 * Renders nothing visible itself; just hooks into DrawerProvider.
 *
 * Uses a ref for context so re-registering the tab (on screen/solution change)
 * does NOT remount ChatPanel — preserving message state and scroll position.
 */
const ChatDrawerTab = ({ instanceId, scenarioId, currentScreen, deviceCode }) => {
  const drawer = useDrawerOptional();
  const { activeSolution } = useSolutionContext();
  const { t, language } = useTranslation();

  // Keep context in a ref so the render callback always sees the latest values
  // without needing to re-register (and remount) the tab
  const contextRef = useRef({ instanceId, scenarioId, currentScreen, activeSolution });
  contextRef.current = { instanceId, scenarioId, currentScreen, activeSolution };

  const registerTab = drawer?.registerTab;
  const unregisterTab = drawer?.unregisterTab;
  const setTabTip = drawer?.setTabTip;
  const activeTab = drawer?.activeTab;

  // Register once per (instanceId, deviceCode) — the conversation identity.
  // Context changes (screen, solution) are picked up via ref, no remount needed.
  useEffect(() => {
    if (!registerTab || !unregisterTab) return undefined;
    registerTab({
      id: 'chat',
      label: t('drawer.chat'),
      icon: <MessageCircle size={18} />,
      color: 'rgba(76, 175, 80, 0.3)',
      borderColor: 'rgba(76, 175, 80, 0.5)',
      order: 1,
      persistent: true, // stays mounted when switching to other tabs
      // key: a new instance is a new conversation — remount so history reloads
      render: () => <ChatPanel key={instanceId} contextRef={contextRef} deviceCode={deviceCode} />,
    });

    return () => unregisterTab('chat');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId, deviceCode, language, registerTab, unregisterTab]);

  // `discoveredVersion` bumps when the user clears the chat — that's our
  // signal to re-evaluate visibility and ignore any prior `discovered`
  // flag in localStorage. Otherwise the bubble stays hidden after the
  // first chat open.
  const [discoveredVersion, setDiscoveredVersion] = useState(0);

  useEffect(() => {
    const handler = () => {
      try { localStorage.removeItem(CHAT_DISCOVERED_KEY); } catch { /* storage unavailable */ }
      setDiscoveredVersion(v => v + 1);
    };
    window.addEventListener('kuutio:chat-cleared', handler);
    return () => window.removeEventListener('kuutio:chat-cleared', handler);
  }, []);

  // The "discovered" flag must only be set on the *open transition* (closed
  // → 'chat'), not whenever the tab is active. Otherwise clearing the
  // history while the tab is open instantly re-sets the flag (the
  // discoveredVersion-driven re-render lands while activeTab is still
  // 'chat'), and the bubble never comes back.
  const prevActiveTab = useRef(activeTab);
  useEffect(() => {
    if (activeTab === 'chat' && prevActiveTab.current !== 'chat') {
      try { localStorage.setItem(CHAT_DISCOVERED_KEY, '1'); } catch { /* storage unavailable */ }
    }
    prevActiveTab.current = activeTab;
  }, [activeTab]);

  // Tip bubble visibility.
  // Show "Kokeile Kuutio-apuria" when:
  //   - the user hasn't opened the chat yet on this device (no discovered flag), AND
  //   - the chat has no messages.
  // Hide while the tab is open. The discovered flag persists across navigations
  // and is reset only by the "kuutio:chat-cleared" event so clearing the
  // conversation makes the bubble testable again on the next drawer close.
  useEffect(() => {
    if (!setTabTip) return undefined;
    if (!instanceId) return undefined;

    if (activeTab === 'chat') {
      setTabTip('chat', null);
      return undefined;
    }

    let discovered = false;
    try { discovered = localStorage.getItem(CHAT_DISCOVERED_KEY) === '1'; } catch { /* storage unavailable */ }
    if (discovered) {
      setTabTip('chat', null);
      return undefined;
    }

    let cancelled = false;
    const userId = getUserId();
    fetch(`${API_ENDPOINTS.RAG_CHAT_HISTORY(userId)}?instanceId=${instanceId}`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled) return;
        const hasMessages = Array.isArray(data?.messages) && data.messages.length > 0;
        setTabTip('chat', hasMessages ? null : {
          id: 'drawer.chat',
          text: t('chatAgent.tryTip'),
          dismissLabel: t('tooltip.dismissSession'),
          closeLabel: t('tooltip.close'),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setTabTip('chat', {
          id: 'drawer.chat',
          text: t('chatAgent.tryTip'),
          dismissLabel: t('tooltip.dismissSession'),
          closeLabel: t('tooltip.close'),
        });
      });
    return () => { cancelled = true; };
  }, [instanceId, activeTab, discoveredVersion, setTabTip, t, language]);

  // HMR can leave consumers mounted against a stale context module — skip
  // rather than crashing the whole app with useDrawer().
  if (!drawer) return null;

  return null;
};

export default ChatDrawerTab;
